import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Calendar, Clock, Plus, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const ALL_SLOTS = [];
for (let h = 8; h < 18; h++) { for (const m of ['00', '30']) { ALL_SLOTS.push(`${String(h).padStart(2, '0')}:${m}`); } }

export const DoctorAvailability = () => {
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [availabilities, setAvailabilities] = useState([]);
  const [currentSlots, setCurrentSlots] = useState([]);
  const [viewMonth, setViewMonth] = useState(() => new Date().toISOString().slice(0, 7));

  const fetchMonthAvailabilities = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/doctor/availability?month=${viewMonth}`, { withCredentials: true });
      setAvailabilities(data);
    } catch (error) { console.error(error); }
  }, [viewMonth]);

  const fetchDateSlots = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/doctor/availability?date=${selectedDate}`, { withCredentials: true });
      setCurrentSlots(data?.slots || []);
    } catch (error) { console.error(error); }
  }, [selectedDate]);

  useEffect(() => { fetchMonthAvailabilities(); }, [fetchMonthAvailabilities]);
  useEffect(() => { fetchDateSlots(); }, [fetchDateSlots]);

  const toggleSlot = (slot) => {
    setCurrentSlots(prev => prev.includes(slot) ? prev.filter(s => s !== slot) : [...prev, slot].sort());
  };

  const selectAll = () => setCurrentSlots([...ALL_SLOTS]);
  const clearAll = () => setCurrentSlots([]);

  const handleSave = async () => {
    try {
      await axios.post(`${API_URL}/api/doctor/availability`, { date: selectedDate, slots: currentSlots }, { withCredentials: true });
      toast.success(`Disponibilidade salva para ${new Date(selectedDate + 'T12:00').toLocaleDateString('pt-BR')}`);
      fetchMonthAvailabilities();
    } catch (error) { toast.error('Erro ao salvar'); }
  };

  const handleCopyToWeek = async () => {
    const baseDate = new Date(selectedDate + 'T12:00');
    let saved = 0;
    for (let i = 1; i <= 6; i++) {
      const next = new Date(baseDate);
      next.setDate(next.getDate() + i);
      if (next.getDay() === 0 || next.getDay() === 6) continue;
      const dateStr = next.toISOString().slice(0, 10);
      try {
        await axios.post(`${API_URL}/api/doctor/availability`, { date: dateStr, slots: currentSlots }, { withCredentials: true });
        saved++;
      } catch (e) { /* skip */ }
    }
    toast.success(`Copiado para ${saved} dias úteis da semana`);
    fetchMonthAvailabilities();
  };

  const datesWithSlots = new Set(availabilities.map(a => a.date));

  // Build calendar grid for the month
  const [year, month] = viewMonth.split('-').map(Number);
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  const daysInMonth = lastDay.getDate();
  const startWeekday = firstDay.getDay();
  const monthNames = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

  return (
    <div className="space-y-6" data-testid="doctor-availability">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-medium text-[#0F1B3D]" style={{ fontFamily: 'Outfit, sans-serif' }}>Disponibilidade</h2>
          <p className="text-[#5F6B85]">Configure sua agenda para os pacientes agendarem</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calendar */}
        <Card className="border-[#E5DCC8]">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={() => { const d = new Date(year, month - 2, 1); setViewMonth(d.toISOString().slice(0,7)); }}>
                &lt;
              </Button>
              <CardTitle className="text-lg">{monthNames[month - 1]} {year}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => { const d = new Date(year, month, 1); setViewMonth(d.toISOString().slice(0,7)); }}>
                &gt;
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-1 text-center text-xs text-[#7A85A0] mb-2">
              {['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'].map(d => <span key={d} className="py-1">{d}</span>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: startWeekday }, (_, i) => <div key={`e${i}`} />)}
              {Array.from({ length: daysInMonth }, (_, i) => {
                const day = i + 1;
                const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                const hasSlots = datesWithSlots.has(dateStr);
                const isSelected = dateStr === selectedDate;
                const isToday = dateStr === new Date().toISOString().slice(0,10);
                return (
                  <button
                    key={day}
                    onClick={() => setSelectedDate(dateStr)}
                    className={`p-2 text-sm rounded-lg transition-all duration-200 relative ${
                      isSelected ? 'bg-[#0F1B3D] text-white font-bold' :
                      isToday ? 'bg-[#FAF6EE] font-semibold text-[#0F1B3D]' :
                      'hover:bg-[#FAF6EE] text-[#0F1B3D]'
                    }`}
                    data-testid={`cal-day-${dateStr}`}
                  >
                    {day}
                    {hasSlots && <span className={`absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-white' : 'bg-[#0F1B3D]'}`} />}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-4 mt-4 text-xs text-[#7A85A0]">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#0F1B3D]" /> Com horários</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#0F1B3D]" /> Selecionado</span>
            </div>
          </CardContent>
        </Card>

        {/* Slot Selector */}
        <Card className="border-[#E5DCC8]">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-[#0F1B3D]" />
              {new Date(selectedDate + 'T12:00').toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
            </CardTitle>
            <div className="flex gap-2 mt-2">
              <Button variant="outline" size="sm" onClick={selectAll} className="text-xs border-[#E5DCC8]" data-testid="select-all-slots">Selecionar Todos</Button>
              <Button variant="outline" size="sm" onClick={clearAll} className="text-xs border-[#E5DCC8]" data-testid="clear-all-slots">Limpar</Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 mb-4">
              {ALL_SLOTS.map(slot => (
                <button
                  key={slot}
                  onClick={() => toggleSlot(slot)}
                  className={`py-2 px-3 text-sm rounded-lg border transition-all duration-200 ${
                    currentSlots.includes(slot)
                      ? 'bg-[#0F1B3D] text-white border-[#0F1B3D]'
                      : 'border-[#E5DCC8] text-[#5F6B85] hover:border-[#0F1B3D]'
                  }`}
                  data-testid={`slot-${slot.replace(':', '')}`}
                >
                  {slot}
                </button>
              ))}
            </div>
            <p className="text-sm text-[#7A85A0] mb-4">{currentSlots.length} horários selecionados</p>
            <div className="flex gap-2">
              <Button onClick={handleSave} className="flex-1 bg-[#A8174E] hover:bg-[#8E1142] text-white" data-testid="save-availability">
                <Save className="h-4 w-4 mr-2" />
                Salvar
              </Button>
              <Button variant="outline" onClick={handleCopyToWeek} className="border-[#E5DCC8]" data-testid="copy-to-week">
                Copiar p/ semana
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
